import { getMarketMovers } from '@simplywallst/services';
import type { QueryFunctionContext } from '@tanstack/react-query';
import {
  formatterCurrencyTruncated,
  formatterFactor,
  formatterPercentage,
} from '@/utilities/format';
import { getBestValuationRatio } from '@/utilities/getBestValuationRatio';
import type { BestValuationRatio } from '@/utilities/getBestValuationRatio';
import { PRIMARY_INDUSTRIES, SECTORS } from '@/constants/industries';
import type { PrimaryCountryISO } from '@/constants/countries';
type CompanyResponse = Awaited<
  ReturnType<typeof getMarketMovers>
>['data']['data']['top'][number];
interface Company {
  companyId: string;
  tickerSymbol: string;
  snowflakePoints: number[];
  shortName: string;
  canonicalUrl: string;
  shortDescription: string;
  return7D: number | null;
  return7DFormatted: string;
  return1YrAbs: number | null;
  return1YrAbsFormatted: string;
  marketCapMovement7DFormatted: string;
  bestValuationRatio: BestValuationRatio;
  peFormatted: string;
  pbFormatted: string;
  psFormatted: string;
  sectorSlug: string;
  uniqueSymbol: string;
  sector: string;
  sectorId: number | null;
  lastSharePriceFormatted: string;
  lastSharePrice: number | null;
}

export interface Response {
  top: Company[];
  worst: Company[];
}

export const getDefault = (): Response => ({
  top: [],
  worst: [],
});

const companyMapper = (company: CompanyResponse): Company | undefined => {
  const industryId = company.primary_industry_id;
  const industry =
    industryId !== null && typeof industryId !== 'undefined'
      ? PRIMARY_INDUSTRIES[industryId]
      : null;
  const industryLabel = industry ? industry.label : '';

  const sectorId = company.sector_industry_id;
  const sector =
    sectorId !== null && typeof sectorId !== 'undefined'
      ? SECTORS[sectorId]
      : null;
  const sectorLabel = sector ? sector.label : '';
  const sectorSlug = sector ? sector.slug : '';
  const bestValuationRatio = getBestValuationRatio(
    company.pb,
    company.pe,
    company.price_to_sales,
    industryLabel
  );
  try {
    const { score } = company;
    return {
      companyId: company.id,
      tickerSymbol: company.ticker_symbol,
      shortName: company.name,
      canonicalUrl: company.canonical_url,
      snowflakePoints: [
        score.value,
        score.future,
        score.past,
        score.health,
        score.dividend,
      ].map((score) => score + 1),
      uniqueSymbol: company.unique_symbol,
      return7D: company.return_7d,
      return7DFormatted: formatterPercentage(company.return_7d),
      return1YrAbs: company.return_1yr_abs,
      return1YrAbsFormatted: formatterPercentage(company.return_1yr_abs),
      bestValuationRatio,
      peFormatted: formatterFactor(company.pe),
      pbFormatted: formatterFactor(company.pb),
      psFormatted: formatterFactor(company.price_to_sales),
      sector: sectorLabel,
      sectorId,
      sectorSlug: sectorSlug as string,
      shortDescription: company.short_description,
      marketCapMovement7DFormatted: company.currency_iso
        ? formatterCurrencyTruncated(
            company.market_cap_movement_7d,
            company.currency_iso,
            1
          )
        : formatterCurrencyTruncated(
            company.market_cap_movement_usd_7d,
            'USD',
            1
          ),
      lastSharePriceFormatted: formatterCurrencyTruncated(
        company.last_share_price,
        company.currency_iso as string
      ),
      lastSharePrice: company.last_share_price,
    };
  } catch (e) {
    console.warn(e);
  }
};

export const MARKET_MOVER_QUERY_KEY = 'market-mover';

export default async function fetchMarketMovers({
  queryKey: [_key, market, industryId],
}: QueryFunctionContext<[string, PrimaryCountryISO, number]>): Promise<
  Response | undefined
> {
  try {
    const {
      data: {
        data: { top, worst },
      },
    } = await getMarketMovers(market, industryId > 0 ? industryId : undefined);

    return {
      top: top
        .map(companyMapper)
        .filter((n): n is Company => typeof n !== 'undefined'),
      worst: worst
        .map(companyMapper)
        .filter((n): n is Company => typeof n !== 'undefined'),
    };
  } catch (e) {
    if (e instanceof Error) {
      console.error(e.message);
      throw new Error(e.message);
    }
  }
}
